import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

const INFINITE_SCROLL_PRELOAD_DISTANCE = 1000;
const LOAD_MORE_FALLBACK_BATCH_SIZE = 30;
const LOAD_MORE_ROW_COUNT = 7;
const LOAD_MORE_FETCH_CONCURRENCY = 4;
const LOAD_MORE_FETCH_RETRY_COUNT = 2;
const LOAD_MORE_FETCH_RETRY_DELAY = 250;
const PRODUCT_TYPE_PRIORITY = [
  'JB4 Tuners',
  'Stage 1 Tuners',
  'JB+ Tuners',
  'Pedal Tuners',
  'Intakes',
  'Air Filters',
  'Charge Pipes',
  'Turbo Inlets & Turbo Accessories',
  'Flex Fuel Kits',
  'Cooling & Heat Shields',
  'Port Injection & Manifolds',
  'Blow Off Valves & Adapters',
  'Oil Catch Cans',
  'Engine Accessories',
  'Strut Braces',
  'Billet Accessories',
  'Wheel Spacers & Accessories',
  'Spark Plugs & Accessories',
  'Chassis Reinforcement & Accessories',
  'Exhaust Tips',
  'Transmission Coolers',
  'Clutch Stops',
  'Fuel Pumps',
  'Sensors',
  'OBDII Dongles & Accessories',
  'License Plate Accessories',
  'Universal Methanol Injection',
  'Dragy & Dragy Accessories',
  'JB4 Device Connections',
  'Universal Products',
];

const PRODUCT_TYPE_RANK = new Map(
  PRODUCT_TYPE_PRIORITY.map((productType, index) => [productType.toLowerCase(), index])
);

const EASYSEARCH_TAG_PATTERN = /(^|-)esr\d+$|^nm-es-/i;

const PRODUCT_TYPE_ALIASES = new Map([
  ['methanol injection', 'universal methanol injection'],
]);

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [loadMoreButton] - The button that loads the next page.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {number | undefined} */
  #scrollCheckFrame;

  #isRenderingNextPage = false;

  #usesBufferedLoadMore = false;

  #loadMoreBuffer = [];

  #loadMoreNextPage = 1;

  #loadMoreLastPage = 1;

  #loadMoreBatchSize = LOAD_MORE_FALLBACK_BATCH_SIZE;

  #loadMoreSeenIds = new Set();

  #loadMoreInitialization;

  #loadMoreGeneration = 0;

  #filterUpdateObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#usesBufferedLoadMore = this.getAttribute('load-more-enabled') === 'true';

    if (this.#usesBufferedLoadMore) {
      this.#startBufferedLoadMore();
    } else {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();

      if (this.refs.viewMoreNext) {
        window.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    window.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollCheckFrame !== undefined) {
      cancelAnimationFrame(this.#scrollCheckFrame);
    }
    this.#loadMoreGeneration += 1;
    this.#filterUpdateObserver?.disconnect();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: `${INFINITE_SCROLL_PRELOAD_DISTANCE}px 0px`,
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #handleWindowScroll = () => {
    this.#scheduleViewMoreCheck();
  };

  #scheduleViewMoreCheck() {
    if (this.#scrollCheckFrame !== undefined) return;

    this.#scrollCheckFrame = requestAnimationFrame(() => {
      this.#scrollCheckFrame = undefined;
      this.#loadPassedViewMoreMarkers();
    });
  }

  #loadPassedViewMoreMarkers() {
    const { viewMoreNext } = this.refs;

    if (
      viewMoreNext &&
      viewMoreNext.getBoundingClientRect().top <= window.innerHeight + INFINITE_SCROLL_PRELOAD_DISTANCE
    ) {
      this.#renderNextPage();
    }
  }

  async loadMore() {
    const { loadMoreButton } = this.refs;

    if (!(loadMoreButton instanceof HTMLButtonElement) || loadMoreButton.disabled) return;

    loadMoreButton.disabled = true;
    loadMoreButton.setAttribute('aria-busy', 'true');

    try {
      if (this.#usesBufferedLoadMore) {
        await this.#loadMoreInitialization;
        await this.#renderBufferedLoadMoreBatch();
      } else {
        await this.#renderNextPage(false);
        loadMoreButton.hidden = !this.#shouldUsePage(this.#getPage('next'));
      }
    } finally {
      if (loadMoreButton.isConnected) {
        loadMoreButton.disabled = false;
        loadMoreButton.removeAttribute('aria-busy');
      }
    }
  }

  async refreshLoadMoreLayout() {
    if (!this.#usesBufferedLoadMore) return;

    await this.#loadMoreInitialization;

    const { grid } = this.refs;
    if (!(grid instanceof HTMLUListElement)) return;

    const columnCount = this.#getGridColumnCount(grid);
    if (columnCount < 1) return;

    this.#loadMoreBatchSize = columnCount * LOAD_MORE_ROW_COUNT;

    const visibleCards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    const incompleteRowCount = visibleCards.length % columnCount;
    if (incompleteRowCount === 0) return;

    const cardsNeededToCompleteRow = columnCount - incompleteRowCount;

    if (this.#loadMoreBuffer.length >= cardsNeededToCompleteRow && cardsNeededToCompleteRow <= incompleteRowCount) {
      this.#appendBufferedCards(cardsNeededToCompleteRow);
    } else {
      const cardsToBuffer = visibleCards.slice(-incompleteRowCount);
      for (const card of cardsToBuffer) card.remove();
      this.#loadMoreBuffer.unshift(...cardsToBuffer);
    }

    this.#updateLoadMoreButton();
  }

  #startBufferedLoadMore() {
    const generation = ++this.#loadMoreGeneration;

    this.#loadMoreInitialization = this.#initializeBufferedLoadMore(generation).catch((error) => {
      if (generation !== this.#loadMoreGeneration) return;

      this.#setProductGridLoading(false);
      console.error('Unable to initialize collection ordering. Falling back to Shopify pagination.', error);
      this.#usesBufferedLoadMore = false;
      this.#fetchPage('next');
    });
  }

  async #initializeBufferedLoadMore(generation) {
    const { grid, loadMoreButton } = this.refs;

    if (!(grid instanceof HTMLUListElement)) return;

    let loadMoreBuffer = [];
    const loadMoreSeenIds = new Set();
    const loadMoreLastPage = Number(grid.dataset.lastPage) || 1;
    const loadMoreBatchSize = this.#getLoadMoreBatchSize(grid);

    const initialCards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    const initialPage = Number(initialCards[0]?.dataset.page) || 1;
    const hasLoadMoreButton = loadMoreButton instanceof HTMLButtonElement;
    let loadMoreNextPage = initialPage + 1;

    this.#setProductGridLoading(true);

    for (const card of initialCards) {
      const cardId = this.#getCardId(card);
      if (cardId) loadMoreSeenIds.add(cardId);
    }

    if (this.#shouldApplyPrioritySorting()) {
      grid.classList.add('product-grid--priority-sorting');
      grid.setAttribute('aria-busy', 'true');

      if (loadMoreButton instanceof HTMLButtonElement) {
        loadMoreButton.disabled = true;
        loadMoreButton.setAttribute('aria-busy', 'true');
      }

      try {
        const pages = Array.from({ length: loadMoreLastPage }, (_, index) => index + 1).filter(
          (page) => page !== initialPage
        );
        const fetchedCards = await this.#fetchLoadMorePages(pages);
        if (generation !== this.#loadMoreGeneration) return;

        const sortedCards = this.#sortPriorityCards([...initialCards, ...fetchedCards]);
        const visibleCardLimit = hasLoadMoreButton ? loadMoreBatchSize : sortedCards.length;
        const visibleCards = sortedCards.slice(0, visibleCardLimit);

        loadMoreBuffer = sortedCards.slice(visibleCardLimit);
        loadMoreNextPage = loadMoreLastPage + 1;

        if (this.isConnected) {
          grid.replaceChildren(...visibleCards);
          this.#aspectRatioHelper?.processNewElements();
        }
      } finally {
        if (generation === this.#loadMoreGeneration) {
          grid.classList.remove('product-grid--priority-sorting');
          this.#setProductGridLoading(false);

          if (loadMoreButton instanceof HTMLButtonElement && loadMoreButton.isConnected) {
            loadMoreButton.disabled = false;
            loadMoreButton.removeAttribute('aria-busy');
          }
        }
      }
    } else if (hasLoadMoreButton && initialCards.length > loadMoreBatchSize) {
      const visibleCards = initialCards.slice(0, loadMoreBatchSize);
      loadMoreBuffer = initialCards.slice(loadMoreBatchSize);
      grid.replaceChildren(...visibleCards);
    } else if (hasLoadMoreButton && initialCards.length < loadMoreBatchSize) {
      const missingCardCount = loadMoreBatchSize - initialCards.length;

      while (loadMoreBuffer.length < missingCardCount && loadMoreNextPage <= loadMoreLastPage) {
        const cards = await this.#fetchLoadMorePage(loadMoreNextPage);
        if (generation !== this.#loadMoreGeneration) return;

        loadMoreNextPage += 1;

        for (const card of cards) {
          const cardId = this.#getCardId(card);
          if (cardId && loadMoreSeenIds.has(cardId)) continue;
          if (cardId) loadMoreSeenIds.add(cardId);
          loadMoreBuffer.push(card);
        }
      }

      const cardsToAppend = loadMoreBuffer.splice(0, missingCardCount);
      grid.append(...cardsToAppend);
      this.#aspectRatioHelper?.processNewElements();
    }

    if (generation !== this.#loadMoreGeneration) return;

    this.#loadMoreBuffer = loadMoreBuffer;
    this.#loadMoreSeenIds = loadMoreSeenIds;
    this.#loadMoreLastPage = loadMoreLastPage;
    this.#loadMoreBatchSize = loadMoreBatchSize;
    this.#loadMoreNextPage = loadMoreNextPage;
    this.#setProductGridLoading(false);
    this.#updateLoadMoreButton();
  }

  #setProductGridLoading(isLoading) {
    this.toggleAttribute('product-grid-loading', isLoading);

    const { grid } = this.refs;
    if (!(grid instanceof HTMLUListElement)) return;

    if (isLoading) {
      grid.setAttribute('aria-busy', 'true');
    } else {
      grid.removeAttribute('aria-busy');
    }
  }

  async #renderBufferedLoadMoreBatch() {
    await this.#fillLoadMoreBuffer(this.#loadMoreBatchSize);
    this.#appendBufferedCards(this.#loadMoreBatchSize);
    this.#updateLoadMoreButton();
  }

  async #fillLoadMoreBuffer(minimumCards) {
    while (this.#loadMoreBuffer.length < minimumCards && this.#loadMoreNextPage <= this.#loadMoreLastPage) {
      const cards = await this.#fetchLoadMorePage(this.#loadMoreNextPage);
      this.#loadMoreNextPage += 1;

      for (const card of cards) {
        const cardId = this.#getCardId(card);
        if (cardId && this.#loadMoreSeenIds.has(cardId)) continue;
        if (cardId) this.#loadMoreSeenIds.add(cardId);
        this.#loadMoreBuffer.push(card);
      }
    }
  }

  #appendBufferedCards(limit) {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLUListElement) || !this.isConnected) return;

    const cards = this.#loadMoreBuffer.splice(0, limit);
    if (cards.length === 0) return;

    grid.append(...cards);
    this.#aspectRatioHelper?.processNewElements();
  }

  #updateLoadMoreButton() {
    const { loadMoreButton } = this.refs;
    if (!(loadMoreButton instanceof HTMLButtonElement)) return;

    loadMoreButton.hidden =
      this.#loadMoreBuffer.length === 0 && this.#loadMoreNextPage > this.#loadMoreLastPage;
  }

  async #fetchLoadMorePage(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    url.hash = '';

    for (let attempt = 0; attempt <= LOAD_MORE_FETCH_RETRY_COUNT; attempt += 1) {
      try {
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
        const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
        const grid = parsedPage.querySelector('[ref="grid"]');

        if (!grid) throw new Error(`Product grid missing from collection page ${pageNumber}`);

        return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
      } catch (error) {
        if (attempt === LOAD_MORE_FETCH_RETRY_COUNT) throw error;
        await new Promise((resolve) => setTimeout(resolve, LOAD_MORE_FETCH_RETRY_DELAY * 2 ** attempt));
      }
    }

    return [];
  }

  async #fetchLoadMorePages(pageNumbers) {
    if (pageNumbers.length === 0) return [];

    const pageResults = new Map();
    let nextIndex = 0;
    const workerCount = Math.min(LOAD_MORE_FETCH_CONCURRENCY, pageNumbers.length);
    const workers = Array.from({ length: workerCount }, async () => {
      while (nextIndex < pageNumbers.length) {
        const pageNumber = pageNumbers[nextIndex];
        nextIndex += 1;
        pageResults.set(pageNumber, await this.#fetchLoadMorePage(pageNumber));
      }
    });

    await Promise.all(workers);

    return pageNumbers.flatMap((pageNumber) => pageResults.get(pageNumber) || []);
  }

  #sortPriorityCards(cards) {
    const uniqueCards = [];
    const seenIds = new Set();

    for (const card of cards) {
      const cardId = this.#getCardId(card);
      if (cardId && seenIds.has(cardId)) continue;
      if (cardId) seenIds.add(cardId);
      uniqueCards.push(card);
    }

    return uniqueCards
      .map((card, index) => ({ card, index }))
      .sort((a, b) => {
        const typeDifference = this.#getProductTypeRank(a.card) - this.#getProductTypeRank(b.card);
        if (typeDifference !== 0) return typeDifference;

        const priceDifference = this.#getProductPrice(b.card) - this.#getProductPrice(a.card);
        if (priceDifference !== 0) return priceDifference;

        return a.index - b.index;
      })
      .map(({ card }) => card);
  }

  #getCardId(card) {
    return card.getAttribute('data-product-id') || card.id || '';
  }

  #getLoadMoreBatchSize(grid) {
    const columnCount = this.#getGridColumnCount(grid);

    if (columnCount > 0) return columnCount * LOAD_MORE_ROW_COUNT;
    return Number(grid.dataset.pageSize) || LOAD_MORE_FALLBACK_BATCH_SIZE;
  }

  #getGridColumnCount(grid) {
    return getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
  }

  #getProductTypeRank(card) {
    const rawProductType = card
      .querySelector('[data-product-type]')
      ?.getAttribute('data-product-type')
      ?.trim()
      .toLowerCase();
    const productType = PRODUCT_TYPE_ALIASES.get(rawProductType || '') || rawProductType;
    if (productType === 'universal products') return Number.MAX_SAFE_INTEGER;
    return PRODUCT_TYPE_RANK.get(productType || '') ?? Number.MAX_SAFE_INTEGER - 1;
  }

  #getProductPrice(card) {
    const productPrice = card.querySelector('[data-product-price]')?.getAttribute('data-product-price');
    return Number(productPrice) || 0;
  }

  /**
   * Priority (type, then price) ordering is only for EasySearch YMM results.
   * A vehicle search lands on a collection URL whose `filter.p.tag` params carry
   * EasySearch tags (e.g. `bmw-...-esr9785977`, `nm-es-universal`). Regular
   * collections keep the order set in the Shopify admin.
   */
  #shouldApplyPrioritySorting() {
    if (!window.location.pathname.includes('/collections/')) return false;

    const params = new URLSearchParams(window.location.search);

    return params.getAll('filter.p.tag').some((tag) => EASYSEARCH_TAG_PATTERN.test(tag));
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @param {boolean} updateHistory - Whether to update the browser URL.
   */
  async #renderNextPage(updateHistory = true) {
    if (this.#isRenderingNextPage) return;

    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    this.#isRenderingNextPage = true;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      if (updateHistory) {
        history.pushState('', '', nextPage.url.toString());
      }

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });

      this.#scheduleViewMoreCheck();
    } finally {
      this.#isRenderingNextPage = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#loadMoreGeneration += 1;
    this.#filterUpdateObserver?.disconnect();
    this.#setProductGridLoading(true);

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const { grid } = this.refs;
    if (grid) {
      const loadMoreButton = this.refs.loadMoreButton;
      if (loadMoreButton instanceof HTMLButtonElement) {
        loadMoreButton.disabled = true;
        loadMoreButton.setAttribute('aria-busy', 'true');
      }

      const observer = new MutationObserver(() => {
        observer.disconnect();
        if (this.#filterUpdateObserver === observer) this.#filterUpdateObserver = undefined;

        requestAnimationFrame(() => {
          if (!this.isConnected) return;

          this.#usesBufferedLoadMore = this.getAttribute('load-more-enabled') === 'true';

          if (this.#usesBufferedLoadMore) {
            this.#startBufferedLoadMore();
          } else {
            this.#observeViewMore();
            this.#fetchPage('next');
          }
        });
      });

      this.#filterUpdateObserver = observer;
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
        subtree: true,
      });

      setTimeout(() => {
        observer.disconnect();
        if (this.#filterUpdateObserver === observer) {
          this.#filterUpdateObserver = undefined;
          this.#setProductGridLoading(false);

          if (loadMoreButton instanceof HTMLButtonElement && loadMoreButton.isConnected) {
            loadMoreButton.disabled = false;
            loadMoreButton.removeAttribute('aria-busy');
          }
        }
      }, 3000);
    }
  };
}
